import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * HelpDashboard — in-app help centre.
 * Sidebar: modules grouped by category.
 * Main: tabs Quickstart | How-To | FAQ | Troubleshooting.
 * Top: search bar + EN/AR toggle.
 */

// ── Palette (mirrors frontend/src/styles/design-tokens) ───────────
const C = {
  bg:      "#060e20",
  glass:   "rgba(255,255,255,0.02)",
  border:  "rgba(189,178,255,0.15)",
  accent:  "#bdb2ff",
  green:   "#cfeda2",
  warn:    "#ffd6a5",
  danger:  "#ff6e84",
  text:    "#dde5ff",
  muted:   "#6d758b",
};

const glass: React.CSSProperties = {
  background: C.glass,
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: `0.5px solid ${C.border}`,
  borderRadius: 16,
  padding: 20,
};

// ── Module groups (mirror Sidebar categorisation) ─────────────────
const MODULE_GROUPS: { label: string; modules: { slug: string; label: string }[] }[] = [
  {
    label: "Sales",
    modules: [
      { slug: "crm",         label: "CRM" },
      { slug: "whatsapp",    label: "WhatsApp" },
      { slug: "voicesales",  label: "Voice Sales" },
      { slug: "omnivoice",   label: "OmniVoice" },
      { slug: "emailintel",  label: "Email Intelligence" },
    ],
  },
  {
    label: "Ops",
    modules: [
      { slug: "inventory",   label: "Inventory" },
      { slug: "marketplace", label: "Marketplace" },
      { slug: "accounting",  label: "AfaqBooks" },
      { slug: "commission",  label: "Commission" },
    ],
  },
  {
    label: "Intel",
    modules: [
      { slug: "seo",         label: "SEO" },
      { slug: "marketintel", label: "Market Intel" },
    ],
  },
  {
    label: "Automation",
    modules: [
      { slug: "content",     label: "Content Automation" },
      { slug: "agentshub",   label: "Agents Hub" },
    ],
  },
  {
    label: "People",
    modules: [
      { slug: "hr",          label: "HR" },
      { slug: "attendance",  label: "Attendance" },
    ],
  },
];

const ALL_SLUGS = MODULE_GROUPS.flatMap(g => g.modules.map(m => m.slug));

type Kind = "quickstart" | "howto" | "faq" | "troubleshooting";

const TAB_LABELS: Record<Kind, { en: string; ar: string }> = {
  quickstart:      { en: "Quick Start",     ar: "البداية السريعة" },
  howto:           { en: "How-To",          ar: "كيف تفعل" },
  faq:             { en: "FAQ",             ar: "أسئلة شائعة" },
  troubleshooting: { en: "Troubleshooting", ar: "حل المشكلات" },
};

interface Article {
  id: string;
  module_slug: string;
  locale: string;
  kind: string;
  title: string;
  body: string;
  order_index: number;
  tags?: string[] | null;
}

// ── Minimal markdown → HTML renderer ─────────────────────────────
// Handles # headings, ** bold, bullet lists, code fences. Enough for
// Veridian-generated help without pulling a markdown dependency.
function renderMarkdown(src: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const lines = src.split("\n");
  const out: string[] = [];
  let inList = false;
  let inCode = false;
  const codeBuf: string[] = [];

  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };

  for (const rawLine of lines) {
    if (rawLine.trim().startsWith("```")) {
      if (inCode) {
        out.push(`<pre style="background:rgba(189,178,255,0.08);padding:10px;border-radius:8px;overflow:auto;font-family:monospace;font-size:12px"><code>${esc(codeBuf.join("\n"))}</code></pre>`);
        codeBuf.length = 0;
        inCode = false;
      } else {
        flushList();
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeBuf.push(rawLine); continue; }

    const m3 = /^###\s+(.*)$/.exec(rawLine);
    const m2 = /^##\s+(.*)$/.exec(rawLine);
    const m1 = /^#\s+(.*)$/.exec(rawLine);
    if (m1 || m2 || m3) {
      flushList();
      const text = (m1?.[1] || m2?.[1] || m3?.[1]) ?? "";
      const size = m1 ? 18 : m2 ? 16 : 14;
      out.push(`<h${m1 ? 3 : m2 ? 4 : 5} style="margin:14px 0 6px 0;color:${C.accent};font-size:${size}px;font-weight:700">${esc(text)}</h${m1 ? 3 : m2 ? 4 : 5}>`);
      continue;
    }

    const li = /^\s*[-*]\s+(.*)$/.exec(rawLine) || /^\s*\d+\.\s+(.*)$/.exec(rawLine);
    if (li) {
      if (!inList) { out.push(`<ul style="padding-left:20px;margin:6px 0;line-height:1.6">`); inList = true; }
      out.push(`<li>${inline(li[1])}</li>`);
      continue;
    }

    if (rawLine.trim() === "") { flushList(); out.push("<div style='height:6px'></div>"); continue; }

    flushList();
    out.push(`<p style="margin:4px 0;line-height:1.6">${inline(rawLine)}</p>`);
  }
  if (inCode) { out.push(`<pre>${esc(codeBuf.join("\n"))}</pre>`); }
  flushList();
  return out.join("\n");

  function inline(s: string): string {
    let t = esc(s);
    t = t.replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${C.text}">$1</strong>`);
    t = t.replace(/`([^`]+)`/g, `<code style="background:rgba(189,178,255,0.12);padding:1px 5px;border-radius:4px;font-family:monospace;font-size:12px">$1</code>`);
    return t;
  }
}

// ── Component ─────────────────────────────────────────────────────
const HelpDashboard: React.FC = () => {
  const defaultLocale =
    (typeof navigator !== "undefined" && /^ar\b/i.test(navigator.language || "")) ? "ar" : "en";

  const [locale, setLocale]         = useState<"en" | "ar">(defaultLocale as "en" | "ar");
  const [activeSlug, setActiveSlug] = useState<string>(ALL_SLUGS[0]);
  const [tab, setTab]               = useState<Kind>("quickstart");
  const [articles, setArticles]     = useState<Article[]>([]);
  const [loading, setLoading]       = useState(false);
  const [searchQ, setSearchQ]       = useState("");
  const [searchResults, setSearchResults] = useState<Article[] | null>(null);
  const [moduleStats, setModuleStats] = useState<Record<string, number>>({});

  // Load per-module article counts (once)
  useEffect(() => {
    fetch("/api/help/modules", { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        const stats: Record<string, number> = {};
        (rows || []).forEach((r: any) => { stats[r.slug] = r.article_count; });
        setModuleStats(stats);
      })
      .catch(() => setModuleStats({}));
  }, []);

  // Load articles for active module
  const loadArticles = useCallback(async () => {
    if (searchResults) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/help/articles?module=${activeSlug}&locale=${locale}&limit=200`,
        { credentials: "include" },
      );
      const data = res.ok ? await res.json() : [];
      setArticles(Array.isArray(data) ? data : []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [activeSlug, locale, searchResults]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  // Debounced search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await fetch(
          `/api/help/search?q=${encodeURIComponent(searchQ.trim())}&locale=${locale}&limit=30`,
          { credentials: "include" },
        );
        const data = r.ok ? await r.json() : { articles: [] };
        setSearchResults(data.articles || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQ, locale]);

  const articlesByKind = useMemo(() => {
    const list = searchResults ?? articles;
    const grouped: Record<Kind, Article[]> = {
      quickstart: [], howto: [], faq: [], troubleshooting: [],
    };
    for (const a of list) {
      if (a.kind in grouped) grouped[a.kind as Kind].push(a);
    }
    return grouped;
  }, [articles, searchResults]);

  const visibleArticles = searchResults ?? articlesByKind[tab];

  const isRTL = locale === "ar";

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        minHeight: "100vh",
        padding: 24,
        background: C.bg,
        color: C.text,
        fontFamily: "'Space Grotesk', sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontFamily: "'Manrope',sans-serif", fontSize: 28, color: C.accent }}>
            {locale === "en" ? "Help Centre" : "مركز المساعدة"}
          </h1>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>
            {locale === "en"
              ? "Quick-starts, how-tos, FAQ and troubleshooting for every module."
              : "أدلة البداية السريعة، الإرشادات، الأسئلة الشائعة، وحل المشكلات."}
          </div>
        </div>

        {/* Language toggle */}
        <div style={{ display: "flex", gap: 4, background: C.glass, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 3 }}>
          {(["en", "ar"] as const).map(l => (
            <button
              key={l}
              onClick={() => setLocale(l)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontFamily: "'Space Grotesk',sans-serif",
                fontWeight: 600,
                fontSize: 12,
                background: locale === l ? C.accent : "transparent",
                color: locale === l ? C.bg : C.muted,
              }}
            >
              {l === "en" ? "EN" : "AR"}
            </button>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div style={{ ...glass, marginBottom: 16, display: "flex", alignItems: "center", gap: 10, padding: "10px 16px" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: C.muted }}>search</span>
        <input
          type="text"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder={locale === "en" ? "Search all help…" : "ابحث في المساعدة…"}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: C.text,
            fontFamily: "inherit",
            fontSize: 14,
          }}
        />
        {searchQ && (
          <button
            onClick={() => setSearchQ("")}
            style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 18 }}
          >
            ×
          </button>
        )}
      </div>

      {/* Two-pane layout */}
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "start" }}>
        {/* Sidebar: modules */}
        <aside style={{ ...glass, padding: 12 }}>
          {MODULE_GROUPS.map(g => (
            <div key={g.label} style={{ marginBottom: 14 }}>
              <div style={{
                color: C.muted, fontSize: 10, letterSpacing: "0.12em",
                textTransform: "uppercase", padding: "4px 8px", marginBottom: 4,
              }}>
                {g.label}
              </div>
              {g.modules.map(m => {
                const active = m.slug === activeSlug && !searchResults;
                const count = moduleStats[m.slug] ?? 0;
                return (
                  <button
                    key={m.slug}
                    onClick={() => { setActiveSlug(m.slug); setSearchQ(""); setTab("quickstart"); }}
                    style={{
                      width: "100%",
                      textAlign: isRTL ? "right" : "left",
                      padding: "7px 10px",
                      marginBottom: 2,
                      borderRadius: 8,
                      border: "none",
                      cursor: "pointer",
                      background: active ? "rgba(189,178,255,0.15)" : "transparent",
                      color: active ? C.accent : C.text,
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{m.label}</span>
                    {count > 0 && (
                      <span style={{
                        fontSize: 10, color: C.muted,
                        background: "rgba(189,178,255,0.08)",
                        padding: "1px 6px", borderRadius: 8,
                      }}>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Main pane */}
        <section style={{ ...glass, minHeight: 500 }}>
          {/* Tabs — hidden in search mode */}
          {!searchResults && (
            <div style={{ display: "flex", gap: 4, marginBottom: 18, borderBottom: `0.5px solid ${C.border}`, paddingBottom: 10 }}>
              {(Object.keys(TAB_LABELS) as Kind[]).map(k => {
                const count = articlesByKind[k].length;
                const active = k === tab;
                return (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      background: active ? "rgba(189,178,255,0.15)" : "transparent",
                      color: active ? C.accent : C.muted,
                    }}
                  >
                    {TAB_LABELS[k][locale]} {count ? <span style={{ opacity: 0.6, fontSize: 11 }}>({count})</span> : null}
                  </button>
                );
              })}
            </div>
          )}

          {searchResults && (
            <div style={{ marginBottom: 14, color: C.muted, fontSize: 12 }}>
              {locale === "en"
                ? `${searchResults.length} results for "${searchQ}"`
                : `${searchResults.length} نتيجة لـ "${searchQ}"`}
            </div>
          )}

          {loading && (
            <div style={{ color: C.muted, padding: 40, textAlign: "center" }}>
              {locale === "en" ? "Loading…" : "جارٍ التحميل…"}
            </div>
          )}

          {!loading && visibleArticles.length === 0 && (
            <div style={{ color: C.muted, padding: 40, textAlign: "center", fontSize: 14 }}>
              {locale === "en"
                ? "No articles yet for this section."
                : "لا توجد مقالات بعد في هذا القسم."}
            </div>
          )}

          {!loading && visibleArticles.map(a => (
            <article
              key={a.id}
              style={{
                marginBottom: 22,
                padding: 16,
                background: "rgba(189,178,255,0.03)",
                border: `0.5px solid ${C.border}`,
                borderRadius: 12,
              }}
            >
              <h2 style={{
                margin: "0 0 10px 0", fontFamily: "'Manrope',sans-serif",
                fontSize: 17, color: C.text,
              }}>
                {a.title}
              </h2>
              {searchResults && (
                <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>
                  {a.module_slug} · {TAB_LABELS[a.kind as Kind]?.[locale] ?? a.kind}
                </div>
              )}
              <div
                style={{ fontSize: 13.5, color: C.text, opacity: 0.9 }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(a.body) }}
              />
              {a.tags && a.tags.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {a.tags.map(t => (
                    <span key={t} style={{
                      fontSize: 10, color: C.green, padding: "2px 8px",
                      background: "rgba(207,237,162,0.1)", borderRadius: 10,
                    }}>
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
};

export default HelpDashboard;
